<template>
    <div class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <statistics-card v-if="canVerifyInstallations" title="Awaiting Verification" :count="getInstallationCount('awaiting_verification')" bg-color="bg-yellow-100" icon-color="text-yellow-700" :href="buildStatusUrl('awaiting_verification')">
            <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l.01 0" /><path d="M13 12l2 0" /><path d="M9 16l.01 0" /><path d="M13 16l2 0" /></svg>
            </template>
        </statistics-card>

        <statistics-card v-if="canVerifyInstallations" title="In Progress" :count="getInstallationCount('in_progress')" bg-color="bg-blue-100" icon-color="text-blue-600" :href="buildStatusUrl('in_progress')">
            <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 21h6" /><path d="M9 21v-18l-6 6h18" /><path d="M9 3l10 6" /><path d="M17 9v4a2 2 0 1 1 -2 2" /></svg>
            </template>
        </statistics-card>

        <statistics-card title="Completed" :count="getInstallationCount('completed')" bg-color="bg-green-100" icon-color="text-green-600" :href="buildStatusUrl('completed')">
            <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 12l2 2l4 -4" /></svg>
            </template>
        </statistics-card>

        <statistics-card v-if="canVerifyInstallations" title="Not Started" :count="getInstallationCount('not_started')" bg-color="bg-gray-100" icon-color="text-gray-600" :href="buildStatusUrl('not_started')">
            <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /></svg>
            </template>
        </statistics-card>
    </div>

    <div class="mb-6 border-b border-gray-200">
        <nav class="-mb-px flex space-x-8">
            <a :href="buildStatusUrl('awaiting_verification')" :class="['whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors no-underline hover:no-underline', activeStatus === 'awaiting_verification' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']">
                Awaiting Verification
                <span :class="[
                        'ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        activeStatus === 'awaiting_verification' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ getInstallationCount('awaiting_verification') }}
                </span>
            </a>

            <a :href="buildStatusUrl('in_progress')" :class="['whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors no-underline hover:no-underline', activeStatus === 'in_progress' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']">
                In Progress
                <span :class="[
                        'ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        activeStatus === 'in_progress' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ getInstallationCount('in_progress') }}
                </span>
            </a>

            <a :href="buildStatusUrl('completed')" :class="['whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors no-underline hover:no-underline', activeStatus === 'completed' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']">
                Completed
                <span :class="[
                        'ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        activeStatus === 'completed' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ getInstallationCount('completed') }}
                </span>
            </a>

            <a v-if="canVerifyInstallations" :href="buildStatusUrl('not_started')" :class="['whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors no-underline hover:no-underline', activeStatus === 'not_started' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']">
                Not Started
                <span :class="[
                        'ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        activeStatus === 'not_started' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    ]">
                    {{ getInstallationCount('not_started') }}
                </span>
            </a>
        </nav>
    </div>

    <installation-table v-if="activeStatus === 'awaiting_verification' && getInstallationData('awaiting_verification').length > 0" title="Awaiting Verification" :installations="installations.awaiting_verification" :show-company-column="isGlobalView">
        <template #name-after="{ installation }">
            <installation-type-badge
                :label="getInstallationTypeLabel(installation.installation_type)"
                :variant="getInstallationTypeVariant(installation.installation_type)"
            >
                <template #icon v-if="getInstallationTypeIcon(installation.installation_type)">
                    <span v-html="getInstallationTypeIcon(installation.installation_type)"></span>
                </template>
            </installation-type-badge>
        </template>

        <template #additional-headers>
            <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="isGlobalView ? 'w-1/4' : 'w-1/3'">Status</th>
        </template>

        <template #additional-columns="{ installation }">
            <td class="px-6 py-4">
                <verification-badge label="Awaiting Verification" :subtitle="installation.verified_by ? `Submitted by ${installation.verified_by}` : null" variant="yellow">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-yellow-600"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l.01 0" /><path d="M13 12l2 0" /><path d="M9 16l.01 0" /><path d="M13 16l2 0" /></svg>
                    </template>
                </verification-badge>
            </td>
        </template>

        <template #actions="{ installation }">
            <div class="flex gap-2">
                <a :href="`/nova/resources/kickstart-installation-resources/${installation.id}`" target="_blank" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-teal-700/20 hover:bg-teal-50 hover:text-teal-700 hover:no-underline focus:text-teal-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:border-teal-700/20 active:text-teal-700 active:no-underline">
                    Nova
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
                </a>
                <a :href="buildRoute(`kickstart-installations/${installation.id}`)" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-800 hover:no-underline focus:text-gray-800 focus:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:text-gray-800 active:no-underline">View Installation</a>
            </div>
        </template>
    </installation-table>

    <installation-table v-if="activeStatus === 'in_progress' && getInstallationData('in_progress').length > 0" title="In Progress" :installations="installations.in_progress" :show-company-column="isGlobalView">
        <template #name-after="{ installation }">
            <installation-type-badge
                :label="getInstallationTypeLabel(installation.installation_type)"
                :variant="getInstallationTypeVariant(installation.installation_type)"
            >
                <template #icon v-if="getInstallationTypeIcon(installation.installation_type)">
                    <span v-html="getInstallationTypeIcon(installation.installation_type)"></span>
                </template>
            </installation-type-badge>
        </template>

        <template #additional-headers>
            <th
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                :class="isGlobalView ? 'w-1/4' : 'w-1/3'"
            >
                Progress
            </th>
        </template>

        <template #additional-columns="{ installation }">
            <td class="px-6 py-4">
                <progress-badge :completed-steps="installation.steps?.filter(s => s.completed).length || 0" :total-steps="installation.steps?.length || 0" />
            </td>
        </template>

        <template #actions="{ installation }">
            <div class="flex gap-2">
                <a :href="`/nova/resources/kickstart-installation-resources/${installation.id}`" target="_blank" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-teal-700/20 hover:bg-teal-50 hover:text-teal-700 hover:no-underline focus:text-teal-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:border-teal-700/20 active:text-teal-700 active:no-underline">
                    Nova
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
                </a>

                <a :href="buildRoute(`kickstart-installations/${installation.id}`)" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-blue-700/20 hover:bg-blue-50 hover:text-blue-700 hover:no-underline focus:text-blue-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:border-blue-700/20 active:text-blue-700 active:no-underline">
                    View Details
                </a>
            </div>
        </template>
    </installation-table>

    <installation-table v-if="activeStatus === 'completed' && getInstallationData('completed').length > 0" title="Completed" :installations="installations.completed" :show-company-column="isGlobalView">
        <template #name-after="{ installation }">
            <installation-type-badge
                :label="getInstallationTypeLabel(installation.installation_type)"
                :variant="getInstallationTypeVariant(installation.installation_type)"
            >
                <template #icon v-if="getInstallationTypeIcon(installation.installation_type)">
                    <span v-html="getInstallationTypeIcon(installation.installation_type)"></span>
                </template>
            </installation-type-badge>
        </template>

        <template #additional-headers>
            <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="isGlobalView ? 'w-1/4' : 'w-1/3'">Verified By</th>
        </template>

        <template #additional-columns="{ installation }">
            <td class="px-6 py-4">
                <verification-badge :label="installation.verifier?.display_name || 'Verified'" :subtitle="installation.verified_at ? new Date(installation.verified_at).toLocaleString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : null" variant="green">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-green-600"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M9 12l2 2l4 -4"/></svg>
                    </template>
                </verification-badge>
            </td>
        </template>

        <template #actions="{ installation }">
            <div class="flex gap-2">
                <a v-if="canVerifyInstallations" :href="`/nova/resources/kickstart-installation-resources/${installation.id}`" target="_blank" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-teal-700/20 hover:bg-teal-50 hover:text-teal-700 hover:no-underline focus:text-teal-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:border-teal-700/20 active:text-teal-700 active:no-underline">
                    Nova
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
                </a>

                <a :href="buildRoute(`kickstart-installations/${installation.id}/report`)" target="_blank" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-purple-700/20 hover:bg-purple-50 hover:text-purple-700 hover:no-underline focus:text-purple-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 active:border-purple-700/20 active:text-purple-700 active:no-underline">
                    <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    View Report
                </a>
            </div>
        </template>
    </installation-table>

    <installation-table v-if="activeStatus === 'not_started' && getInstallationData('not_started').length > 0" title="Not Started" :installations="installations.not_started" :show-company-column="isGlobalView">
        <template #name-after="{ installation }">
            <installation-type-badge
                :label="getInstallationTypeLabel(installation.installation_type)"
                :variant="getInstallationTypeVariant(installation.installation_type)"
            >
                <template #icon v-if="getInstallationTypeIcon(installation.installation_type)">
                    <span v-html="getInstallationTypeIcon(installation.installation_type)"></span>
                </template>
            </installation-type-badge>
        </template>

        <template #additional-headers>
            <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="isGlobalView ? 'w-1/4' : 'w-1/3'">Status</th>
        </template>

        <template #additional-columns>
            <td class="px-6 py-4">
                <status-badge label="Not Started" variant="gray">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/></svg>
                    </template>
                </status-badge>
            </td>
        </template>

        <template #actions="{ installation }">
            <a :href="`/nova/resources/kickstart-installation-resources/${installation.id}`" target="_blank" class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-teal-700/20 hover:bg-teal-50 hover:text-teal-700 hover:no-underline focus:text-teal-700 focus:no-underline focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:border-teal-700/20 active:text-teal-700 active:no-underline">
                Nova
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
            </a>
        </template>
    </installation-table>

    <empty-state v-if="activeStatus === 'awaiting_verification' && getInstallationData('awaiting_verification').length === 0" title="No Installations Awaiting Verification" message="All installations have been verified or are still in progress">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l.01 0" /><path d="M13 12l2 0" /><path d="M9 16l.01 0" /><path d="M13 16l2 0" /></svg>
        </template>
    </empty-state>

    <empty-state v-if="activeStatus === 'in_progress' && getInstallationData('in_progress').length === 0" title="No Active Installations" message="There are currently no installations in progress">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 21h6" /><path d="M9 21v-18l-6 6h18" /><path d="M9 3l10 6" /><path d="M17 9v4a2 2 0 1 1 -2 2" /></svg>
        </template>
    </empty-state>

    <empty-state v-if="activeStatus === 'completed' && getInstallationData('completed').length === 0" title="No Completed Installations" message="Completed installations will appear here">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 12l2 2l4 -4" /></svg>
        </template>
    </empty-state>

    <empty-state v-if="activeStatus === 'not_started' && getInstallationData('not_started').length === 0" title="All Installations Started" message="There are no installations waiting to begin">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /></svg>
        </template>
    </empty-state>
</template>

<script setup>
import { computed } from 'vue'
import { usePermissions } from '@/composables/usePermissions'
import { useCompany } from '@/composables/useCompany'
import StatisticsCard from '@/components/Kickstart/StatisticsCard.vue'
import InstallationTable from '@/components/Kickstart/InstallationTable.vue'
import EmptyState from '@/components/Kickstart/EmptyState.vue'
import ProgressBadge from '@/components/Kickstart/ProgressBadge.vue'
import StatusBadge from '@/components/Kickstart/StatusBadge.vue'
import VerificationBadge from '@/components/Kickstart/VerificationBadge.vue'
import InstallationTypeBadge from '@/components/Kickstart/InstallationTypeBadge.vue'

/**
 * @typedef {import('@/types/Installation').Installations} Installations
 * @typedef {import('@/types/InstallationType').InstallationType} InstallationType
 */

const props = defineProps({
    /** @type {import('vue').Prop<Installations>} */
    installations: {
        type: Object,
        required: true,
    },
    /** @type {import('vue').Prop<InstallationType[]>} */
    installationTypes: {
        type: Array,
        default: () => [],
    },
    isGlobalView: {
        type: Boolean,
        default: false,
    },
    activeStatus: {
        type: String,
        default: 'awaiting_verification',
    },
    installationType: {
        type: String,
        default: null,
    },
})

const { buildCompanyRoute, getCompanyId } = useCompany()
const { can, loadCompany, isCompanyLoaded } = usePermissions()

const currentCompanyId = getCompanyId()
loadCompany(currentCompanyId)

const canVerifyInstallations = computed(() => {
    if (!isCompanyLoaded(currentCompanyId)) return false
    return can('verify_kickstart_installations') || can('do_anything')
})

/**
 * @param {string} route
 * @returns {string}
 */
const buildRoute = (route) => {
    return buildCompanyRoute(route, currentCompanyId)
}

/**
 * @param {string} status
 * @returns {string}
 */
const buildStatusUrl = (status) => {
    const baseUrl = buildRoute('kickstart-installations')
    const params = new URLSearchParams({ status })

    if (props.installationType) {
        params.append('type', props.installationType)
    }

    return `${baseUrl}?${params.toString()}`
}

/**
 * @param {keyof Installations} key
 * @returns {Installation[]}
 */
const getInstallationData = (key) => {
    const data = props.installations[key]
    if (!data) return []
    return data.data || data
}

/**
 * @param {keyof Installations} key
 * @returns {number}
 */
const getInstallationCount = (key) => {
    const data = props.installations[key]
    if (!data) return 0

    if (data.meta && typeof data.meta.total !== 'undefined') {
        return data.meta.total
    }

    return data.data?.length || data.length || 0
}

/**
 * Get the configuration for an installation type
 * @param {string} type
 * @returns {InstallationType|undefined}
 */
const getInstallationTypeConfig = (type) => {
    return props.installationTypes.find(t => t.key === type)
}

/**
 * Get the display label for an installation type
 * @param {string} type
 * @returns {string}
 */
const getInstallationTypeLabel = (type) => {
    if (!type) return 'Unknown'

    const config = getInstallationTypeConfig(type)
    if (config) {
        return config.label
    }

    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

/**
 * Get the color variant for an installation type badge
 * @param {string} type
 * @returns {string}
 */
const getInstallationTypeVariant = (type) => {
    const config = getInstallationTypeConfig(type)
    return config?.variant || 'gray'
}

/**
 * Get the icon SVG for an installation type
 * @param {string} type
 * @returns {string|undefined}
 */
const getInstallationTypeIcon = (type) => {
    const config = getInstallationTypeConfig(type)
    return config?.icon
}
</script>